import { motion } from "framer-motion";
import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SavingsCalculator = () => {
  const [vessels, setVessels] = useState(10);
  const [fuelSpend, setFuelSpend] = useState(500000);
  const { t, i18n } = useTranslation('home');

  const lowSaving = Math.round(fuelSpend * vessels * 0.08);
  const highSaving = Math.round(fuelSpend * vessels * 0.15);

  const locale = i18n.language?.startsWith('sv') ? 'sv-SE' : 'en-US';
  const formatCurrency = (val: number) =>
    new Intl.NumberFormat(locale, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(val);

  return (
    <section className="py-28 md:py-40 section-fade">
      <div className="container mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7 }}>
          <div className="text-center mb-20">
            <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-4">{t('savings.label')}</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground">
              {t('savings.heading')} <span className="font-extrabold">{t('savings.headingBold')}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div className="space-y-10">
              <div>
                <div className="flex justify-between items-baseline mb-4">
                  <label htmlFor="vessels-range" className="text-sm text-muted-foreground">{t('savings.vessels')}</label>
                  <span className="text-2xl font-bold text-foreground">{vessels}</span>
                </div>
                <input id="vessels-range" type="range" min={1} max={100} value={vessels} onChange={(e) => setVessels(Number(e.target.value))} aria-label={t('savings.vessels')} className="w-full h-1.5 rounded-full appearance-none bg-secondary cursor-pointer accent-primary" />
              </div>
              <div>
                <div className="flex justify-between items-baseline mb-4">
                  <label htmlFor="fuel-spend-range" className="text-sm text-muted-foreground">{t('savings.fuelSpend')}</label>
                  <span className="text-2xl font-bold text-foreground">{formatCurrency(fuelSpend)}</span>
                </div>
                <input id="fuel-spend-range" type="range" min={50000} max={5000000} step={50000} value={fuelSpend} onChange={(e) => setFuelSpend(Number(e.target.value))} aria-label={t('savings.fuelSpend')} className="w-full h-1.5 rounded-full appearance-none bg-secondary cursor-pointer accent-primary" />
              </div>
            </div>
            <div className="text-center md:text-left space-y-4">
              <p className="text-sm text-muted-foreground">{t('savings.resultLabel')}</p>
              <p className="font-display text-2xl sm:text-4xl md:text-5xl font-bold text-foreground break-words">
                {formatCurrency(lowSaving)}–{formatCurrency(highSaving)}
              </p>
              <p className="text-sm text-muted-foreground">{t('savings.resultNote')}</p>
              <div className="pt-4">
                <Link to="/contact" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-md font-medium text-sm hover:bg-primary-hover transition-colors">
                  {t('savings.cta')} <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SavingsCalculator;
